import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    // selectSection,
    // editProduct,
    removeProduct,
    selectNamedProduct,
    setTorsoOverlay,
    removeBuild,
    selectSectionAndCategory,
    setActiveFilterButton,
} from '../../actions';
import { computeTorsoBackImage, resolveReverseHeadImage, REVERSE_HEAD_IMAGE_MAP } from '../../utils/customiseHelpers';

import SelectedProduct from '../../entities/SelectedProduct';
import { checkIncompatibilities, formatIncompatibilityAlert } from '../../utils/incompatibility';
import ProjectPurchase from '../../components/Navigation/ProjectPurchase';
import AccessoriesSlider from './AccessoriesSlider';
import PreviewItem from './PreviewItem';
import Warning from './Warning';
import BuilderMoney from '../BuilderMoney';

const getVariantsArray = (product) => {
    if (!product) return [];
    if (product?.variants?.edges) return product.variants.edges.map((e) => e.node);
    return product?.variants?.nodes || [];
};

const findVariantForSkintone = (selectedPart, targetSkintone) => {
    if (!selectedPart || !targetSkintone)
        return {
            variant: null,
            mismatch: false,
        };

    const target = targetSkintone.toLowerCase();
    const current =
        selectedPart?.productMatchAttr?.skintoneValue?.value?.toLowerCase() ??
        selectedPart?.attributes?.skintoneValue?.value?.toLowerCase();

    if (!current)
        return {
            variant: null,
            mismatch: false,
        };

    if (current === target)
        return {
            variant: null,
            mismatch: false,
        };

    const variants = getVariantsArray(selectedPart.product);
    const variant = variants.find((v) => v?.skintoneValue?.value?.toLowerCase() === target) || null;

    return {
        variant,
        mismatch: true,
    };
};

const handImages = {
    dark: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Dark-Flesh.png?v=1665587356',
    light: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Light-Flesh.png?v=1665587356',
    yellow: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Yellow.png?v=1665587356',
    medium: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Medium-Flesh.png?v=1665587363',
    nougat: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/NougatHands-ForTorsoOverlay.png?v=1721912778',
};

const Preview = ({
    selected = {},
    dispatch,
    projectTitle = '',
    skintone,
    torsoOverlays = {},
    headFeatures = {},
    buying = false,
    currentBuildIndex,
    setEnableBuilder,
    currentBuild,
    onPreviewSurfaceClick,
    torsoImageUpload,
    displayImageUpload,
    navigation,
}) => {
    const [incompatibilities, setIncompatibilities] = useState([]);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showResetWarning, setShowResetWarning] = useState(false);
    const [showBuyWarning, setShowBuyWarning] = useState(false);
    const previewItems = [
        {
            id: 'hatsAndHair',
            title: 'Hats & Hair',
            emptyText: 'Choose a hat or some hair',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-hair-2.png?v=1765287494',
            handle: 'hair-and-hats',
        },
        {
            id: 'heads',
            title: 'Heads',
            emptyText: 'Choose a head',
            // Dynamic blank head matching current skintone; fallback to yellow
            defaultImage:
                REVERSE_HEAD_IMAGE_MAP[(skintone || 'yellow').toLowerCase?.()] || REVERSE_HEAD_IMAGE_MAP['yellow'],
            handle: 'all-heads-new-1',
        },
        {
            id: 'bodies',
            title: 'Bodies',
            emptyText: 'Choose a body',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-torso.png?v=1760018973',
            handle: 'all-torsos-new-website',
        },
        {
            id: 'legs',
            title: 'Legs',
            emptyText: 'Choose a pair of legs',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-legs.png?v=1760018973',
            handle: 'all-legs',
        },
    ];
    const formRef = useRef(null);
    const [headVariantId, setHeadVariantId] = useState([null]);
    const [legVariantId, setLegVariantId] = useState([null]);
    const [showBack, setShowBack] = useState(false);
    const [backImages, setBackImages] = useState({ hatsAndHair: '', head: '', body: '', legs: '' });

    const updateIncompatibility = useCallback((key, label, message, present) => {
        setIncompatibilities((prev) => {
            const exists = prev.find((i) => i.key === key);
            if (present) {
                if (exists) return prev.map((i) => (i.key === key ? { ...i, message, label } : i));
                return [...prev, { key, label, message }];
            }
            if (!exists) return prev;
            return prev.filter((i) => i.key !== key);
        });
    }, []);

    const updateTorsoOverlays = useCallback(() => {
        const handImage = handImages[(skintone || 'yellow').toLowerCase?.() ? skintone?.toLowerCase() : 'yellow'];
        let torsoOverlayImage = '';

        const selectedBody = selected?.bodies;
        if (selectedBody) {
            let activeTorso = selectedBody?.attributes;

            // If no attributes (e.g., direct add from product list), try productMatchAttr or derive from product
            if (!activeTorso) {
                activeTorso = selectedBody?.productMatchAttr;
            }

            if (!activeTorso && selectedBody.product?.variants?.nodes?.length === 1) {
                activeTorso = selectedBody.product.variants.nodes[0];
            }

            if (activeTorso?.torsoOverlayValue?.value) {
                const overlayBase =
                    activeTorso.torsoOverlayValue?.value === 'Light' ? 'Colour' : activeTorso.torsoOverlayValue?.value;
                const imageStringToFind = `${overlayBase}-Artwork---${(skintone || 'yellow').toLowerCase()}`;
                const defaultImageStrings = `${overlayBase}-Artwork---default`;

                selectedBody.product?.images?.edges?.forEach((image) => {
                    const lower = image.node.url.toLowerCase();

                    if (lower.includes(imageStringToFind.toLowerCase())) {
                        torsoOverlayImage = image.node.url;
                    } else if (!torsoOverlayImage && lower.includes(defaultImageStrings.toLowerCase())) {
                        torsoOverlayImage = image.node.url;
                    }
                });
            } else {
                const lowerSkin = (skintone || 'yellow').toLowerCase();
                let candidate = '';
                let defaultCandidate = '';

                selectedBody.product?.images?.edges?.forEach((edge) => {
                    const url = edge?.node?.url || '';
                    const lower = url.toLowerCase();
                    if (!candidate && lower.includes(`-artwork---${lowerSkin}`)) {
                        candidate = url;
                    } else if (!defaultCandidate && lower.includes('-artwork---default')) {
                        defaultCandidate = url;
                    }
                });

                torsoOverlayImage = candidate || defaultCandidate || '';
            }
        }

        dispatch(
            setTorsoOverlay(currentBuildIndex, {
                handImage,
                torsoOverlayImage,
            })
        );
    }, [dispatch, selected?.bodies, skintone, currentBuildIndex]);

    // Heads: auto-update to matching skintone if variant exists; otherwise record incompatibility
    useEffect(() => {
        const { variant, mismatch } = findVariantForSkintone(selected?.heads, skintone);
        if (!mismatch) {
            updateIncompatibility('head-skintone', 'Head', '', false);
            return;
        }
        if (variant) {
            setHeadVariantId(variant.id?.split('/').pop());
            updateIncompatibility('head-skintone', 'Head', '', false);
        } else {
            setHeadVariantId([]);
            updateIncompatibility(
                'head-skintone',
                'Head',
                'Selected head is not available in the chosen skin tone.',
                true
            );
        }
    }, [selected?.heads, skintone, updateIncompatibility]);

    // Legs: auto-update to matching skintone; otherwise mark incompatibility
    useEffect(() => {
        const { variant, mismatch } = findVariantForSkintone(selected?.legs, skintone);
        if (!mismatch) {
            updateIncompatibility('legs-skintone', 'Legs', '', false);
            return;
        }
        if (variant) {
            setLegVariantId(variant.id?.split('/').pop());
            updateIncompatibility('legs-skintone', 'Legs', '', false);
        } else {
            setLegVariantId([]);
            updateIncompatibility(
                'legs-skintone',
                'Legs',
                'Selected legs are not available in the chosen skin tone.',
                true
            );
        }
    }, [selected?.legs, skintone, updateIncompatibility]);

    const updateHeadSkintone = useCallback(
        (headVariant) => {
            const selectedHead = selected?.heads;
            if (!selectedHead) return;
            const headVar = headVariant[0]?.node ? headVariant[0]?.node : headVariant[0];
            if (headVar) {
                if (!selectedHead.productMatchAttr || headVar.id !== selectedHead?.productMatchAttr?.id) {
                    const pfjv = selectedHead.productFormJsonValues
                        ? { ...selectedHead.productFormJsonValues }
                        : { quantity: 1 };
                    pfjv.id = headVar.id;
                    pfjv.variantId = headVar.id;
                    pfjv.variant_id = headVar.id;
                    dispatch(
                        selectNamedProduct(
                            currentBuildIndex,
                            selectedHead.product,
                            null,
                            null,
                            headVar,
                            selectedHead.category,
                            pfjv
                        )
                    );
                }
            } else {
                // incompatibility already tracked in head effect
            }
        },
        [dispatch, selected?.heads, currentBuildIndex]
    );

    useEffect(() => {
        if (updateTorsoOverlays) {
            updateTorsoOverlays();
        }
    }, [skintone, updateTorsoOverlays]);

    useEffect(() => {
        const headProduct = selected?.heads?.product;
        const headVariants = headProduct?.variants?.edges ? headProduct?.variants?.edges : headProduct?.variants?.nodes;
        const headVariant = headVariants?.filter((head) => {
            if (head.node) {
                return head.node.skintoneValue?.value.toLowerCase() === skintone.toLowerCase();
            } else {
                return head.skintoneValue?.value?.toLowerCase() === skintone.toLowerCase();
            }
        });

        updateHeadSkintone(headVariant);
    }, [headVariantId, selected?.heads?.product, skintone, updateHeadSkintone]);

    // Apply selected leg variant when legVariantId changes
    useEffect(() => {
        const selectedLeg = selected?.legs;
        if (!selectedLeg) return;
        const legProduct = selectedLeg.product;
        const legVariants = legProduct?.variants?.edges ? legProduct?.variants?.edges : legProduct?.variants?.nodes;
        const legVariant = legVariants?.filter((leg) => {
            if (leg.node) {
                return leg.node.skintoneValue?.value?.toLowerCase() === skintone.toLowerCase();
            } else {
                return leg.skintoneValue?.value?.toLowerCase() === skintone.toLowerCase();
            }
        });
        if (legVariant && legVariant.length > 0) {
            const legVar = legVariant[0]?.node ? legVariant[0]?.node : legVariant[0];
            if (!selectedLeg.productMatchAttr || legVar.id !== selectedLeg?.productMatchAttr?.id) {
                const pfjv = selectedLeg.productFormJsonValues
                    ? { ...selectedLeg.productFormJsonValues }
                    : { quantity: 1 };
                pfjv.id = legVar.id;
                pfjv.variantId = legVar.id;
                pfjv.variant_id = legVar.id;
                dispatch(
                    selectNamedProduct(
                        currentBuildIndex,
                        selectedLeg.product,
                        null,
                        null,
                        legVar,
                        selectedLeg.category,
                        pfjv
                    )
                );
            }
        }
    }, [legVariantId, selected?.legs, skintone, dispatch, currentBuildIndex]);

    const accessoryInfo = {
        title: 'Accessory',
        emptyText: 'Add an accessory',
        defaultImage: '',
        handle: 'all-accessories',
    };

    // Accessories button edit
    const onEdit = () => {
        const accessoriesInfo = { id: 'accessories', title: 'Accessory', handle: 'all-accessories' };
        dispatch(selectSectionAndCategory(accessoriesInfo, null, skintone));
        dispatch(setActiveFilterButton(null));
        const browser = document.querySelector('.browser');
        browser?.scrollIntoView({ behavior: 'smooth' });
    };

    const onRemove = (sectionId) => dispatch(removeProduct(currentBuildIndex, sectionId));

    const handleRotatePreview = () => {
        if (!showBack) {
            const nextBackImages = { hatsAndHair: '', head: '', body: '', legs: '' };

            // Torso / Body
            const bodySel = selected?.bodies;
            if (bodySel?.product) {
                try {
                    nextBackImages.body =
                        computeTorsoBackImage(
                            bodySel.product,
                            bodySel.attributes || bodySel.productMatchAttr,
                            skintone
                        ) || '';
                } catch (e) {
                    // ignore
                }
            }

            // Head
            const headSel = selected?.heads;
            if (headSel) {
                const explicitHeadBack = headSel?.productMatchAttr?.reverseImage?.reference?.image?.url;
                if (explicitHeadBack) {
                    nextBackImages.head = explicitHeadBack;
                } else {
                    try {
                        const fallback = resolveReverseHeadImage(
                            'heads',
                            headSel.productMatchAttr || headSel.attributes,
                            skintone
                        );
                        nextBackImages.head = fallback?.reference?.image?.url || '';
                    } catch (e) {
                        // ignore
                    }
                }
            }

            // Legs
            const legSel = selected?.legs;
            if (legSel) {
                nextBackImages.legs = legSel?.productMatchAttr?.reverseImage?.reference?.image?.url || '';
            }

            // Hats and Hair
            const hatsAndHairSel = selected?.hatsAndHair;
            if (hatsAndHairSel) {
                nextBackImages.hatsAndHair =
                    hatsAndHairSel?.productMatchAttr?.reverseImage?.reference?.image?.url || '';
            }

            setBackImages(nextBackImages);
            setShowBack(true);
        } else {
            setShowBack(false);
        }
    };

    // Flip to front whenever any selected item changes
    const selectionSignature = useMemo(() => {
        const core = ['hatsAndHair', 'heads', 'bodies', 'legs', 'display', 'keychain'];
        const coreSig = core
            .map((k) => {
                const item = selected?.[k];
                if (!item) return `${k}:_`;
                const variantId = item.productMatchAttr?.id || item.attributes?.id || item.product?.id || '_';
                return `${k}:${variantId}`;
            })
            .join('|');

        const accessoriesSig = Array.isArray(selected?.accessories)
            ? selected.accessories
                  .map((acc) => acc?.productMatchAttr?.id || acc?.attributes?.id || acc?.product?.id || '_')
                  .join(',')
            : '_';
        return `${coreSig}|acc:${accessoriesSig}`;
    }, [selected]);

    const prevSelectionSignatureRef = useRef(selectionSignature);
    useEffect(() => {
        if (prevSelectionSignatureRef.current && prevSelectionSignatureRef.current !== selectionSignature) {
            if (showBack) setShowBack(false);
        }
        prevSelectionSignatureRef.current = selectionSignature;
    }, [selectionSignature, showBack]);

    // Derive hair/accessory incompatibilities on each render (lightweight) using existing util.
    const derivedAccessoryHairIncompatibilities = useCallback(() => {
        if (!currentBuild) return [];
        const warnings = [];

        // Candidate as existing hair against each accessory (simulate adding accessory) to surface conflicts.
        if (selected?.hatsAndHair && selected.hatsAndHair.product && Array.isArray(selected?.accessories)) {
            const accessoryConflicts = [];
            selected.accessories.forEach((acc) => {
                const result = checkIncompatibilities({
                    currentBuild: { selected },
                    candidateProduct: acc.product,
                    candidateSection: 'accessories',
                });
                if (result) {
                    const { message: text } = formatIncompatibilityAlert(result);
                    if (text) {
                        accessoryConflicts.push({ id: acc.product?.id, text });
                    }
                }
            });
            if (accessoryConflicts.length) {
                // Combine all accessory conflict texts (dedupe identical lines)
                const uniqueMessages = Array.from(new Set(accessoryConflicts.map((c) => c.text.trim())));
                warnings.push({
                    key: `acc-group-${selected.hatsAndHair.product.id}`,
                    label: 'Hair/Accessories',
                    message: uniqueMessages.join('\n\n'),
                });
            }
        }

        // Check display item for incompatibilities
        if (selected?.display?.product) {
            const result = checkIncompatibilities({
                currentBuild: { selected },
                candidateProduct: selected.display.product,
                candidateSection: 'display',
            });
            if (result) {
                const { message: text } = formatIncompatibilityAlert(result);
                if (text) {
                    warnings.push({
                        key: `display-${selected.display.product?.id}`,
                        label: 'Display',
                        message: text,
                    });
                }
            }
        }

        // Check keychain item for incompatibilities
        if (selected?.keychain?.product) {
            const result = checkIncompatibilities({
                currentBuild: { selected },
                candidateProduct: selected.keychain.product,
                candidateSection: 'keychain',
            });
            if (result) {
                const { message: text } = formatIncompatibilityAlert(result);
                if (text) {
                    warnings.push({
                        key: `keychain-${selected.keychain.product?.id}`,
                        label: 'Keychain',
                        message: text,
                    });
                }
            }
        }

        return warnings;
    }, [currentBuild, selected]);

    const combinedIncompatibilities = [...incompatibilities, ...derivedAccessoryHairIncompatibilities()].filter(
        (v, idx, arr) => arr.findIndex((o) => o.key === v.key) === idx
    );
    const selectedDisplayProductId = selected?.display?.product?.id;
    const displayImageUploadMatchesSelectedProduct =
        !!selectedDisplayProductId &&
        !!displayImageUpload?.productId &&
        displayImageUpload.productId === selectedDisplayProductId;
    const displayImageUploadForSelectedProduct = displayImageUploadMatchesSelectedProduct ? displayImageUpload : null;

    return (
        <section className={`${buying ? 'buying' : ''} preview-section`} onClick={onPreviewSurfaceClick}>
            <div className="checkoutPriceBar">
                <span className="preview-total">
                    {combinedIncompatibilities.length > 0 && (
                        <button
                            className="build-info"
                            onClick={() => setShowInfoModal(true)}
                            aria-label="View build incompatibilities"
                        >
                            <img
                                src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-information.svg?v=1759312072"
                                alt="Build incompatibilities present"
                            />
                        </button>
                    )}
                    <b>
                        <BuilderMoney amount={SelectedProduct?.calculateCost(selected)} />
                    </b>
                </span>
                <ProjectPurchase setEnableBuilder={setEnableBuilder} />
            </div>
            <article className={`preview-container ${navigation.selected}`}>
                <div className="preview-items">
                    {previewItems.map((pItem) => (
                        <PreviewItem
                            key={pItem.id}
                            item={selected[pItem.id]}
                            info={pItem}
                            dispatch={dispatch}
                            // onEdit={onEdit}
                            onRemove={onRemove}
                            skintone={skintone}
                            torsoOverlays={torsoOverlays}
                            headFeatures={pItem.id === 'heads' ? headFeatures : null}
                            showBack={showBack}
                            overrideImage={
                                showBack
                                    ? pItem.id === 'heads'
                                        ? backImages.head
                                        : pItem.id === 'bodies'
                                          ? backImages.body
                                          : pItem.id === 'legs'
                                            ? backImages.legs
                                            : pItem.id === 'hatsAndHair'
                                              ? backImages.hatsAndHair
                                              : undefined
                                    : undefined
                            }
                            compositeDataUri={
                                pItem.id === 'bodies'
                                    ? torsoImageUpload?.compositeDataUri
                                    : pItem.id === 'display'
                                      ? displayImageUploadForSelectedProduct?.compositeDataUri
                                      : undefined
                            }
                            overlayImageSrc={
                                pItem.id === 'display'
                                    ? displayImageUploadForSelectedProduct?.compositeDataUri
                                    : undefined
                            }
                        />
                    ))}
                </div>

                <div style={{ display: 'none' }}>
                    <h2>Head replacement form</h2>
                    <form
                        action="/cart/add"
                        method="post"
                        encType="multipart/form-data"
                        id={`AddToCartForm-${headVariantId}`}
                        ref={formRef}
                    >
                        <input type="number" name="quantity" value="1" hidden readOnly />
                        <input name="id" value={headVariantId} hidden readOnly />
                        <input
                            type="text"
                            id={`projectTitle-${headVariantId}`}
                            name="properties[projectTitle]"
                            value={projectTitle ? projectTitle : ''}
                            hidden
                            readOnly
                        />
                    </form>
                </div>
                {showInfoModal && (
                    <Warning
                        title="Incompatibilities"
                        actionConfirm={() => setShowInfoModal(false)}
                        confirmLabel="Close"
                        warningText={
                            <ul className="builder-incompatibility-list">
                                {combinedIncompatibilities.map((inc) => (
                                    <li key={inc.key}>
                                        <strong>{inc.label}:</strong> {inc.message}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                )}

                {showBuyWarning && (
                    <Warning
                        actionConfirm={() => {
                            setShowBuyWarning(false);
                        }}
                        warningText="Please select a head, body, and some legs to continue."
                    />
                )}

                <button className="reset-button" onClick={() => setShowResetWarning(true)}>
                    Clear all
                </button>

                <button
                    onClick={handleRotatePreview}
                    className={`preview-mode-button ${showBack ? 'is-back' : 'is-front'}`}
                    aria-pressed={showBack}
                    aria-label={showBack ? 'Show front of minifig' : 'Show back of minifig'}
                >
                    <img
                        src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-front-arrow.svg?v=1760432921"
                        alt="Rotate figure"
                    />
                    <span>{showBack ? 'Back' : 'Front'}</span>
                </button>

                {showResetWarning && (
                    <div className="reset-overlay">
                        <Warning
                            actionConfirm={() => {
                                dispatch(removeBuild(currentBuildIndex));
                                setShowResetWarning(false);
                            }}
                            actionDeny={() => {
                                setShowResetWarning(false);
                            }}
                            warningText="Are you sure you want to start again?"
                        />
                    </div>
                )}

                <PreviewItem
                    key={'display'}
                    item={selected['display']}
                    info={{
                        id: 'display',
                        title: 'Display',
                        emptyText: 'Add a display',
                        defaultImage:
                            'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-display_64e00823-a12c-4282-aa68-d6e041c98343.svg?v=1760431334',
                        handle: 'all-displays',
                    }}
                    dispatch={dispatch}
                    onRemove={onRemove}
                    skintone={skintone}
                    torsoOverlays={torsoOverlays}
                    showBack={showBack}
                    compositeDataUri={displayImageUploadForSelectedProduct?.compositeDataUri}
                    overlayImageSrc={displayImageUploadForSelectedProduct?.compositeDataUri}
                />

                <PreviewItem
                    key={'keychain'}
                    item={selected['keychain']}
                    info={{
                        id: 'keychain',
                        title: 'Keychain',
                        emptyText: 'Add a keychain',
                        defaultImage:
                            'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/keychain.svg?v=1760356896',
                        // We still navigate to the display handle to show the list
                        handle: 'all-displays',
                    }}
                    dispatch={dispatch}
                    onRemove={onRemove}
                    skintone={skintone}
                    torsoOverlays={torsoOverlays}
                    showBack={showBack}
                />

                <AccessoriesSlider
                    accessories={Array.isArray(selected?.accessories) ? selected.accessories : []}
                    accessoryInfo={accessoryInfo}
                    dispatch={dispatch}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    skintone={skintone}
                />
            </article>
        </section>
    );
};

const mapStateToProps = (state) => {
    const currentBuild = state.builds[state.currentBuildIndex];
    return {
        currentBuildIndex: state.currentBuildIndex,
        projectTitle: currentBuild.projectTitle,
        navigation: state.navigation,
        skintone: currentBuild.skintone,
        torsoOverlays: currentBuild.torsoOverlays,
        headFeatures: currentBuild.headFeatures,
        torsoImageUpload: currentBuild.torsoImageUpload,
        displayImageUpload: currentBuild.displayImageUpload,
        buying: state.buying,
        currentBuild,
        activeCurrency: state.activeCurrency,
    };
};

export default connect(mapStateToProps)(Preview);

Preview.propTypes = {
    selected: PropTypes.shape({
        hatsAndHair: PropTypes.object,
        heads: PropTypes.object,
        bodies: PropTypes.object,
        legs: PropTypes.object,
        display: PropTypes.object,
        keychain: PropTypes.object,
        accessories: PropTypes.arrayOf(PropTypes.object),
    }),
    dispatch: PropTypes.func.isRequired,
    projectTitle: PropTypes.string,
    skintone: PropTypes.string,
    torsoOverlays: PropTypes.shape({
        handImage: PropTypes.string,
        torsoOverlayImage: PropTypes.string,
    }),
    headFeatures: PropTypes.object,
    torsoImageUpload: PropTypes.shape({
        compositeDataUri: PropTypes.string,
    }),
    displayImageUpload: PropTypes.shape({
        compositeDataUri: PropTypes.string,
        productId: PropTypes.string,
    }),
    buying: PropTypes.bool,
    currentBuildIndex: PropTypes.number.isRequired,
    setEnableBuilder: PropTypes.func.isRequired,
    currentBuild: PropTypes.object,
    onPreviewSurfaceClick: PropTypes.func.isRequired,
    navigation: PropTypes.shape({
        selected: PropTypes.string,
    }),
};
